import { memo, useState } from "react";
import { Box, Button, Grid, styled, Tab, TabContext, TabList, TabPanel } from "~/common/mui";
import { Link } from "~/common/components/Link";
import {
  HousingSATileSelect,
  TileSelectVariant,
} from "~/features/selectionAssistant/housing/components/Inputs/TileSelect/TileSelect";
import { HousingSACheckboxSelect } from "~/features/selectionAssistant/housing/components/Inputs/CheckboxSelect/CheckboxSelect";
import {
  SASection,
  SASectionContents,
  SASectionDescription,
  SASectionTitle,
} from "~/features/selectionAssistant/housing/components/Section/SASection";
import { SingleDimensionInput } from "~/features/selectionAssistant/housing/components/Inputs/DualUnitNumberInput/SingleDimensionInput";
import { useFormContext } from "react-hook-form";
import { type HousingSearchForm } from "~/features/selectionAssistant/housing/types/housingSAFormTypes";
import { TemperatureSwitch, UnitSwitch } from "~/features/selectionAssistant/housing/components/Filters/Switches";
import { selectDimensionTileConfig } from "~/features/selectionAssistant/housing/redux/selectors";
import { useSelector } from "react-redux";
import { HousingMultiSelect } from "~/features/selectionAssistant/housing/components/Inputs/HSAMultiSelect";
import { HousingSAAutocompleteInput } from "~/features/selectionAssistant/housing/components/Inputs/HSAAutosuggestInput";
import { RHFDualSystemNumberInput } from "~/features/selectionAssistant/housing/components/Inputs/DualUnitNumberInput/DualSystemNumberInput";
import {
  type HousingSearchFormKey,
  useHSAResetAction,
} from "~/features/selectionAssistant/housing/context/HousingResetContext";
import { trackTabsChangedHSA } from "~/app/analytics/ga/ga";
import { useHSAFacetData } from "~/features/selectionAssistant/housing/components/useHSAFacetData";
import { useTranslation } from "react-i18next";
import { NumberSystem } from "~/features/selectionAssistant/housing/types/types";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import RestartAltIcon from "@mui/icons-material/RestartAlt";

const FindAssistantButton = styled(Button)(({ theme }) => ({
  backgroundColor: `${theme.palette.common.black} !important`,
  ...theme.typography.bodyMedium,
}));

const FlippedIcon = styled(RestartAltIcon)({
  transform: "scaleX(-1)",
  fontSize: 20,
});

const ResetLink = ({
  fieldsToReset,
  manualReset = false,
}: {
  fieldsToReset: HousingSearchFormKey[];
  manualReset?: boolean;
}) => {
  const { t } = useTranslation();

  const resetData = useHSAResetAction();

  return (
    <Link
      data-test-id={"housing-assistant-reset"}
      className={"mt-auto"}
      onClick={() => resetData(manualReset, ...fieldsToReset)}
    >
      <FlippedIcon className="icon-margin" />
      {t("page.linear.links.reset")}
    </Link>
  );
};

const TabContents = styled(Box)(({ theme }) => ({
  margin: `${theme.spacing(3.75)} 0 ${theme.spacing(1.25)} 0`,
  display: "grid",
  gap: theme.spacing(3.75),
}));

const FirstTabContents = styled(TabContents)({
  gridTemplate: '"bearingSealing design" auto "dimensions dimensions" auto / 1fr 1fr',
  "& > .scha__housing-selection__section--bearing\\&sealing": {
    gridArea: "bearingSealing",
  },
  "& > .scha__housing-selection__section--design": {
    gridArea: "design",
  },
  "& > .scha__housing-selection__section--dimensions": {
    gridArea: "dimensions",
  },
});

const SecondTabContents = styled(TabContents)({
  gridTemplate:
    '"system bearingSealing" auto "temperature housing" auto "bearingMounting housing" auto "housingMaterial housing" auto / 1fr 1fr',
  "& > .scha__housing-selection__section--system": {
    gridArea: "system",
  },
  "& > .scha__housing-selection__section--bearing\\&sealing": {
    gridArea: "bearingSealing",
  },
  "& > .scha__housing-selection__section--housing": {
    gridArea: "housing",
  },
  "& > .scha__housing-selection__section--temperature": {
    gridArea: "temperature",
  },
  "& > .scha__housing-selection__section--bearing-mounting": {
    gridArea: "bearingMounting",
  },
  "& > .scha__housing-selection__section--housing-material": {
    gridArea: "housingMaterial",
  },
});

const DimensionsGrid = styled("div")(({ theme }) => ({
  display: "flex",
  marginTop: theme.spacing(-3.75),
  marginLeft: theme.spacing(-4),
  width: "auto !important",
  marginBottom: theme.spacing(3.75),
  "& > *": {
    paddingTop: theme.spacing(3.75),
    paddingLeft: theme.spacing(4),
    flex: 1,
  },
}));

const DimensionGridItem = styled("div")({
  flex: 1,
});

const DimensionImagesGrid = styled("div")(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "repeat(4, auto) 1fr",
  gap: theme.spacing(4),
  "& > img": {
    width: 166,
    height: 96,
  },
}));

const BearingSealingGrid = styled(Grid)(({ theme }) => ({
  marginTop: theme.spacing(-3.75),
  marginLeft: theme.spacing(-4),
  width: "auto !important",
  "& > *": {
    paddingTop: theme.spacing(3.75),
    paddingLeft: theme.spacing(4),
  },
}));

export const HousingFilters = memo(() => {
  return <DesktopHousingFilters />;
});

const DesktopHousingFilters = () => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState("BearingDesign");
  const formProps = useFormContext<HousingSearchForm>();
  const unit = formProps.watch("dimensionUnit");
  const housingTypeState = formProps.watch("housing_type");
  const housingInnerState = formProps.watch("housingdinner");
  const housingOuterState = formProps.watch("housingdouter");
  const searchDesignationState = formProps.watch("housinginclbrg");
  const dimensionsConfig = useSelector(selectDimensionTileConfig);
  const { values: valuesHousingType } = useHSAFacetData({ name: "housing_type" });

  const renderGaEvent = (
    pushContext:
      | "section_switched"
      | "housing_type_selected"
      | "shaft_diameter_provided"
      | "outside_diameter_provided"
      | "item_from_designation_search_dropdown_selected",
    searchDesignation?: string,
    housingType?: string,
  ) => {
    const selectedHousingType =
      !!valuesHousingType && !!dimensionsConfig
        ? valuesHousingType.filter((item) => item.code === (housingType ? housingType : housingTypeState))[0]
        : undefined;
    const selectedHousingTypeLabel =
      !!selectedHousingType && selectedHousingType.label ? selectedHousingType.label : undefined;

    trackTabsChangedHSA(
      activeTab === "BearingDesign" ? "Bearing & Design Envelope" : "System Characteristics",
      pushContext,
      housingInnerState,
      housingOuterState,
      selectedHousingTypeLabel,
      searchDesignation ? searchDesignation : searchDesignationState,
    );
  };

  const submitFuncHelperGA = () => {
    if (housingInnerState.min !== "" || housingInnerState.max !== "") {
      renderGaEvent("shaft_diameter_provided");
    }
    if (housingOuterState.min !== "" || housingOuterState.max !== "") {
      renderGaEvent("outside_diameter_provided");
    }
  };

  const submitButton = (
    <Button data-test-id={"housing-assistant-apply"} type={"submit"} onClick={() => submitFuncHelperGA()}>
      {t("searchPage.refinement.button.apply")}
    </Button>
  );

  const designationSearch = (
    <HousingSAAutocompleteInput
      name={"housinginclbrg"}
      renderGA={(searchDesignation) =>
        renderGaEvent("item_from_designation_search_dropdown_selected", searchDesignation)
      }
    />
  );

  const bearingCharacteristics = (
    <Grid container className={"mt-60"}>
      <Grid
        size={{
          phone: 6,
        }}
      >
        <HousingSACheckboxSelect className={"mb-30"} name={"bearing_type"} multiple={true} />
      </Grid>
      <Grid
        size={{
          phone: 6,
        }}
      >
        <HousingSACheckboxSelect className={"mb-30"} name={"bearing_design"} multiple={true} />
      </Grid>
    </Grid>
  );

  const bearingResetLink = <ResetLink fieldsToReset={["housinginclbrg", "bearing_type", "bearing_design"]} />;

  const housingCharacteristics = (
    <>
      <HousingSATileSelect
        className={"mb-30"}
        name={"housing_type"}
        renderGA={(value) =>
          renderGaEvent("housing_type_selected", undefined, (value ?? undefined) as string | undefined)
        }
      />

      {!!housingTypeState && (
        <>
          <HousingSATileSelect className={"mb-30"} name={"number_of_bolts"} multiple={true} />

          <HousingSATileSelect className={"mb-30"} name={"housing_shapes"} multiple={true} />
        </>
      )}

      <Grid container className={"mt-1 pt-30"}>
        <Grid
          size={{
            phone: 6,
          }}
        >
          <HousingSACheckboxSelect className={"mb-30"} name={"housing_design"} multiple={true} />
        </Grid>
        <Grid
          size={{
            phone: 6,
          }}
        >
          <HousingMultiSelect name={"sealing_type"} />
        </Grid>
      </Grid>
    </>
  );

  const housingResetLink = (
    <ResetLink
      fieldsToReset={["housing_type", "number_of_bolts", "housing_shapes", "housing_design", "sealing_type"]}
    />
  );

  return (
    <TabContext value={activeTab}>
      <TabList
        variant={"standard"}
        onChange={(e, val) => {
          renderGaEvent("section_switched");
          setActiveTab(val);
        }}
      >
        <Tab
          data-test-id={"housingselectionassistant-bearing-design-envelope"}
          value={"BearingDesign"}
          label={t("page.housingSelectionAssistant.bearing&design")}
        />
        <Tab
          data-test-id={"housingselectionassistant-system-characteristics"}
          value={"SystemCharacteristics"}
          label={t("page.housingSelectionAssistant.systemCharacteristics")}
        />
      </TabList>
      <TabPanel value={"BearingDesign"} keepMounted>
        <FirstTabContents className={"scha__sa__tab-contents scha__housing-selection__first-tab"}>
          <SASection className={"scha__housing-selection__section--bearing&sealing"}>
            <SASectionTitle>{t("page.housingSelectionAssistant.bearingSealing")}</SASectionTitle>
            <SASectionDescription>
              {t("page.housingSelectionAssistant.info.bearingSealingCharacteristics")}
            </SASectionDescription>
            <SASectionContents>
              {designationSearch}
              <div className={"mt-1 pt-2"}>
                <FindAssistantButton
                  href="#dimensions-up"
                  data-test-id={"btn-bore-diameter"}
                  data-ta-spec={"housing_assistant_diameter_scroller"}
                  className={"button"}
                  size={"medium"}
                  startIcon={<ArrowDownwardIcon sx={{ fontSize: 24 }} />}
                >
                  {t("page.housingselectionassistant.btn.enterBoreDia")}
                </FindAssistantButton>
              </div>
              {bearingCharacteristics}

              {bearingResetLink}
            </SASectionContents>
          </SASection>

          <SASection className={"scha__housing-selection__section--design"}>
            <SASectionTitle>{t("page.housingSelectionAssistant.designEnvelope")}</SASectionTitle>
            <SASectionDescription>{t("page.housingSelectionAssistant.info.housingType")}</SASectionDescription>

            <SASectionContents>
              {housingCharacteristics}
              {housingResetLink}
            </SASectionContents>
          </SASection>

          <SASection className={"scha__housing-selection__section--dimensions"} id="dimensions-up">
            <SASectionTitle>{t("page.selectionAssistant.tab.dimensions")}</SASectionTitle>

            <SASectionContents>
              <UnitSwitch className={"mb-60"} />
              <DimensionsGrid className="scha__housing-selection__dimensions-grid">
                {dimensionsConfig.map((techFilter) => (
                  <DimensionGridItem key={techFilter.code} className="scha__housing-selection__dimensions-grid__item">
                    <SingleDimensionInput label={techFilter.label} mode={unit} name={techFilter.code} />
                  </DimensionGridItem>
                ))}
              </DimensionsGrid>
              <DimensionImagesGrid className="scha__housing-selection__dimensions-grid--images">
                {DIMENSION_SECTION_IMAGES.map((imgUrl) => (
                  <img src={imgUrl} />
                ))}
              </DimensionImagesGrid>

              <div className="mt-auto d-flex flex-align-center flex-justify-between">
                <ResetLink
                  fieldsToReset={dimensionsConfig.map((i) => i.code as HousingSearchFormKey)}
                  manualReset={true}
                />
                {submitButton}
              </div>
            </SASectionContents>
          </SASection>
        </FirstTabContents>
      </TabPanel>
      <TabPanel value={"SystemCharacteristics"} keepMounted>
        <SecondTabContents className={"scha__sa__tab-contents scha__housing-selection__second-tab"}>
          <SASection className={"scha__housing-selection__section--system"}>
            <SASectionTitle>{t("page.housingselectionassistant.cluster.system.name")}</SASectionTitle>
            <SASectionDescription>{t("page.housingselectionassistant.cluster.system.subtitle")}</SASectionDescription>
            <SASectionContents>
              <BearingSealingGrid container className={"scha__housing-selection__bearingsealing-grid"}>
                <Grid
                  size={{
                    phone: 6,
                  }}
                >
                  <HousingSATileSelect name={"housingload"} variant={TileSelectVariant.ICN_LEFT} multiple={true} />
                </Grid>

                <Grid
                  size={{
                    phone: 6,
                  }}
                >
                  <HousingSATileSelect
                    name={"housinglubrication"}
                    variant={TileSelectVariant.ICN_LEFT}
                    multiple={true}
                  />
                </Grid>

                <Grid
                  size={{
                    phone: 6,
                  }}
                >
                  <HousingSATileSelect name={"housingcoating"} variant={TileSelectVariant.ICN_LEFT} multiple={true} />
                </Grid>

                <Grid
                  size={{
                    phone: 6,
                  }}
                >
                  <HousingSATileSelect
                    name={"housingmisalignment"}
                    variant={TileSelectVariant.ICN_LEFT}
                    multiple={true}
                  />
                </Grid>
              </BearingSealingGrid>

              <ResetLink
                fieldsToReset={["housingload", "housinglubrication", "housingcoating", "housingmisalignment"]}
              />
            </SASectionContents>
          </SASection>

          <SASection className={"scha__housing-selection__section--bearing&sealing"}>
            <SASectionTitle>{t("page.housingSelectionAssistant.bearingSealing")}</SASectionTitle>
            <SASectionDescription>
              {t("page.housingSelectionAssistant.info.bearingSealingCharacteristics")}
            </SASectionDescription>

            <SASectionContents>
              {designationSearch}

              {bearingCharacteristics}

              {bearingResetLink}
            </SASectionContents>
          </SASection>

          <SASection className={"scha__housing-selection__section--housing"}>
            <SASectionTitle>{t("page.housingselectionassistant.cluster.housing_characteristics.name")}</SASectionTitle>
            <SASectionDescription>
              {t("page.housingselectionassistant.cluster.housing_characteristics.subtitle")}
            </SASectionDescription>

            <SASectionContents>
              <HousingSAAutocompleteInput name={"housinginclhsg"} className={"mb-30"} />

              {housingCharacteristics}
              {housingResetLink}
            </SASectionContents>
          </SASection>

          <SASection className={"scha__housing-selection__section--temperature"}>
            <SASectionTitle>{t("page.housingselectionassistant.cluster.temperature_range.name")}</SASectionTitle>
            <SASectionDescription>
              {t("page.housingselectionassistant.cluster.temperature_range.subtitle")}
            </SASectionDescription>

            <SASectionContents>
              <TemperatureSwitch className={"mb-30"} />

              <DimensionsGrid className={"scha__housing-selection__dimensions-grid"}>
                <div>
                  <RHFDualSystemNumberInput
                    mode={NumberSystem.Metric}
                    data-test-id={"housing-sa-temp-min"}
                    label={t("searchPage.refinement.value.label.min")}
                    name={"housingtempmin.min"}
                  />
                </div>

                <div>
                  <RHFDualSystemNumberInput
                    mode={NumberSystem.Metric}
                    data-test-id={"housing-sa-temp-max"}
                    label={t("searchPage.refinement.value.label.max")}
                    name={"housingtempmin.max"}
                  />
                </div>
              </DimensionsGrid>

              <div className="mt-auto d-flex flex-align-center flex-justify-between">
                <ResetLink fieldsToReset={["housingtempmin"]} manualReset={true} />
                {submitButton}
              </div>
            </SASectionContents>
          </SASection>

          <SASection className={"scha__housing-selection__section--bearing-mounting"}>
            <SASectionTitle>{t("page.housingselectionassistant.cluster.bearing_mounting_type.name")}</SASectionTitle>

            <SASectionContents>
              <HousingSACheckboxSelect
                className={"mb-30"}
                omitLabel={true}
                name={"bearing_mounting_type"}
                columnAmount={2}
                multiple={true}
              />

              <ResetLink fieldsToReset={["bearing_mounting_type"]} />
            </SASectionContents>
          </SASection>

          <SASection className={"scha__housing-selection__section--housing-material"}>
            <SASectionTitle>{t("page.housingselectionassistant.cluster.housing_material.name")}</SASectionTitle>

            <SASectionContents>
              <HousingSACheckboxSelect
                className={"mb-30"}
                omitLabel={true}
                name={"housing_material"}
                columnAmount={2}
                multiple={true}
              />

              <ResetLink fieldsToReset={["housing_material"]} />
            </SASectionContents>
          </SASection>
        </SecondTabContents>
      </TabPanel>
    </TabContext>
  );
};

const DIMENSION_SECTION_IMAGES = [
  "https://cdn.schaeffler-ecommerce.com/cdn/00197793_e.png",
  "https://cdn.schaeffler-ecommerce.com/cdn/00197797_e.png",
  "https://cdn.schaeffler-ecommerce.com/cdn/0019779D_e.png",
  "https://cdn.schaeffler-ecommerce.com/cdn/001977A9_e.png",
];
